import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  GraduationCap, Users, AlertTriangle, CheckCircle, Clock,
  XCircle, Search, Filter, RefreshCw, Calendar, TrendingUp,
  Bell, ChevronRight, Shield, BarChart3, UserCheck, AlertCircle,
  Send, Mail, Settings
} from 'lucide-react';

export default function TrainingCurrencyPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [expiringDays, setExpiringDays] = useState(30);

  const fetchDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      params.append('expiring_days', expiringDays);
      
      const response = await api.get(`/training/currency/dashboard?${params.toString()}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load training currency data');
    }
  }, [api, statusFilter, departmentFilter, expiringDays]);

  const fetchMatrix = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      
      const response = await api.get(`/training/currency/matrix?${params.toString()}`);
      setMatrix(response.data);
    } catch (error) {
      console.error('Failed to fetch matrix:', error);
    }
  }, [api, departmentFilter]);

  const fetchExpiring = useCallback(async () => {
    try {
      const response = await api.get(`/training/currency/expiring?days=${expiringDays}`);
      setExpiring(response.data);
    } catch (error) {
      console.error('Failed to fetch expiring:', error);
    }
  }, [api, expiringDays]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchMatrix(), fetchExpiring()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchMatrix, fetchExpiring]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchMatrix(), fetchExpiring()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  // Send bulk reminders
  const handleSendBulkReminders = async (days) => {
    setSendingReminders(true);
    try {
      const response = await api.post(`/training/reminders/bulk-send?threshold_days=${days}`);
      setReminderStatus(response.data);
      toast.success(`Sent ${response.data.reminders_sent} reminders`);
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error('Failed to send reminders');
    }
    setSendingReminders(false);
  };

  // Run automated reminder check
  const handleRunReminderCheck = async () => {
    setSendingReminders(true);
    try {
      await api.post('/training/reminders/check');
      toast.success('Reminder check started in background');
    } catch (error) {
      console.error('Failed to run reminder check:', error);
      toast.error('Failed to run reminder check');
    }
    setSendingReminders(false);
  };

  // Send individual reminder
  const handleSendIndividualReminder = async (userId, programId, userName, programName) => {
    try {
      await api.post(`/training/reminders/send/${userId}?program_id=${programId}`);
      toast.success(`Reminder sent to ${userName} for ${programName}`);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  // Filter entries by search query
  const filteredEntries = dashboard?.entries?.filter(entry => 
    entry.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Get unique departments for filter
  const departments = [...new Set(dashboard?.entries?.map(e => e.department).filter(Boolean) || [])];

  // Check if user is admin or safety manager
  const canSendReminders = user?.role === 'admin' || user?.role === 'safety_manager';

  const getStatusBadge = (status) => {
    switch (status) {
      case 'current':
        return <Badge className="bg-emerald-600 text-white"><CheckCircle className="w-3 h-3 mr-1" />Current</Badge>;
      case 'expiring_soon':
        return <Badge className="bg-amber-600 text-white"><Clock className="w-3 h-3 mr-1" />Expiring</Badge>;
      case 'expired':
        return <Badge className="bg-red-600 text-white"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      case 'pending_initial':
      case 'pending':
        return <Badge className="bg-slate-600 text-white"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'not_required':
        return <Badge className="bg-slate-500 text-slate-200">N/A</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getComplianceColor = (rate) => {
    if (rate >= 100) return 'text-emerald-400';
    if (rate >= 75) return 'text-yellow-400';
    if (rate >= 50) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="training-currency-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-emerald-500" />
            Training Currency Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Monitor certification status and expiry alerts for all personnel</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="border-slate-600" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Personnel</p>
                <p className="text-2xl font-bold text-white">{dashboard?.summary?.total_users || 0}</p>
              </div>
              <Users className="w-10 h-10 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Fully Compliant</p>
                <p className="text-2xl font-bold text-emerald-400">{dashboard?.summary?.fully_compliant || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Expiring Soon</p>
                <p className="text-2xl font-bold text-amber-400">{dashboard?.summary?.expiring_soon || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Expired</p>
                <p className="text-2xl font-bold text-red-400">{dashboard?.summary?.expired || 0}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending Initial</p>
                <p className="text-2xl font-bold text-slate-400">{dashboard?.summary?.pending_initial || 0}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-slate-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="personnel" className="data-[state=active]:bg-emerald-600">
            <Users className="w-4 h-4 mr-2" />
            Personnel
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-emerald-600">
            <Bell className="w-4 h-4 mr-2" />
            Alerts
            {(dashboard?.expiring_alerts?.length > 0 || dashboard?.expired_alerts?.length > 0) && (
              <Badge className="ml-2 bg-red-600 text-white text-xs">
                {(dashboard?.expiring_alerts?.length || 0) + (dashboard?.expired_alerts?.length || 0)}
              </Badge>
            )}
          </TabsTrigger>
          {canSendReminders && (
            <TabsTrigger value="reminders" className="data-[state=active]:bg-emerald-600">
              <Send className="w-4 h-4 mr-2" />
              Send Reminders
            </TabsTrigger>
          )}
          <TabsTrigger value="matrix" className="data-[state=active]:bg-emerald-600">
            <Shield className="w-4 h-4 mr-2" />
            Training Matrix
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Distribution */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  Compliance Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Fully Compliant (100%)</span>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={(dashboard?.summary?.fully_compliant / dashboard?.summary?.total_users) * 100 || 0} 
                        className="w-32 h-2"
                      />
                      <span className="text-emerald-400 font-medium w-12 text-right">
                        {dashboard?.summary?.fully_compliant || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Partially Compliant</span>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={(dashboard?.summary?.partially_compliant / dashboard?.summary?.total_users) * 100 || 0} 
                        className="w-32 h-2 [&>div]:bg-yellow-500"
                      />
                      <span className="text-yellow-400 font-medium w-12 text-right">
                        {dashboard?.summary?.partially_compliant || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Non-Compliant</span>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={(dashboard?.summary?.non_compliant / dashboard?.summary?.total_users) * 100 || 0} 
                        className="w-32 h-2 [&>div]:bg-red-500"
                      />
                      <span className="text-red-400 font-medium w-12 text-right">
                        {dashboard?.summary?.non_compliant || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Urgent Alerts Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Urgent Attention Required
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Certifications requiring immediate action
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-3">
                    {dashboard?.expired_alerts?.slice(0, 5).map((alert, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-900/20 rounded border border-red-800/50">
                        <div className="flex items-center gap-3">
                          <XCircle className="w-4 h-4 text-red-400" />
                          <div>
                            <p className="text-white text-sm font-medium">{alert.user_name}</p>
                            <p className="text-slate-400 text-xs">{alert.program_name}</p>
                          </div>
                        </div>
                        <Badge className="bg-red-600 text-white text-xs">
                          {alert.days_overdue}d overdue
                        </Badge>
                      </div>
                    ))}
                    {dashboard?.expiring_alerts?.slice(0, 5).map((alert, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-amber-900/20 rounded border border-amber-800/50">
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-amber-400" />
                          <div>
                            <p className="text-white text-sm font-medium">{alert.user_name}</p>
                            <p className="text-slate-400 text-xs">{alert.program_name}</p>
                          </div>
                        </div>
                        <Badge className="bg-amber-600 text-white text-xs">
                          {alert.days_remaining}d left
                        </Badge>
                      </div>
                    ))}
                    {(!dashboard?.expired_alerts?.length && !dashboard?.expiring_alerts?.length) && (
                      <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                        <CheckCircle className="w-12 h-12 mb-2 text-emerald-500" />
                        <p>No urgent alerts</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Personnel Tab */}
        <TabsContent value="personnel" className="space-y-4">
          {/* Filters */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-700"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="compliant">Fully Compliant</SelectItem>
                    <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Personnel List */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {filteredEntries.map((entry) => (
                    <div 
                      key={entry.user_id} 
                      className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-emerald-600 text-white">
                              {entry.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-white font-medium">{entry.user_name}</p>
                            <p className="text-slate-400 text-sm">{entry.user_email}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs border-slate-600">{entry.role}</Badge>
                              {entry.department && (
                                <Badge variant="outline" className="text-xs border-slate-600">{entry.department}</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${getComplianceColor(entry.compliance_rate)}`}>
                            {entry.compliance_rate}%
                          </div>
                          <p className="text-slate-400 text-xs">
                            {entry.mandatory_completed}/{entry.mandatory_total} mandatory
                          </p>
                          <div className="flex gap-1 mt-1 justify-end">
                            {entry.expiring_count > 0 && (
                              <Badge className="bg-amber-600 text-xs">{entry.expiring_count} expiring</Badge>
                            )}
                            {entry.expired_count > 0 && (
                              <Badge className="bg-red-600 text-xs">{entry.expired_count} expired</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Programs breakdown */}
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="flex flex-wrap gap-2">
                          {entry.programs.slice(0, 6).map((prog, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-center gap-1 px-2 py-1 bg-slate-800 rounded text-xs"
                              title={`${prog.program_name}\nExpiry: ${prog.expiry_date ? new Date(prog.expiry_date).toLocaleDateString() : 'N/A'}`}
                            >
                              {getStatusBadge(prog.status)}
                              <span className="text-slate-300 truncate max-w-[100px]">{prog.program_name}</span>
                            </div>
                          ))}
                          {entry.programs.length > 6 && (
                            <Badge variant="outline" className="text-xs">+{entry.programs.length - 6} more</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredEntries.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                      <Users className="w-16 h-16 mb-4 opacity-50" />
                      <p>No personnel found matching your filters</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Expired */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Expired Certifications ({dashboard?.expired_alerts?.length || 0})
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Requires immediate action - personnel may not be authorized to perform duties
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {dashboard?.expired_alerts?.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-red-900/20 rounded border border-red-800/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-red-600 text-white text-xs">
                                {alert.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-white font-medium text-sm">{alert.user_name}</p>
                              <p className="text-red-300 text-xs">{alert.program_name}</p>
                            </div>
                          </div>
                          <Badge className="bg-red-600 text-white">
                            {alert.days_overdue} days overdue
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>Expired: {new Date(alert.expiry_date).toLocaleDateString()}</span>
                          <Button size="sm" variant="outline" className="h-6 text-xs border-red-600 text-red-400 hover:bg-red-900/50">
                            Schedule Training
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!dashboard?.expired_alerts || dashboard.expired_alerts.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <CheckCircle className="w-12 h-12 mb-2 text-emerald-500" />
                        <p>No expired certifications</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Expiring Soon */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-amber-400 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Expiring Soon ({dashboard?.expiring_alerts?.length || 0})
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Within {expiringDays} days - schedule recurrent training
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {dashboard?.expiring_alerts?.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-amber-900/20 rounded border border-amber-800/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-amber-600 text-white text-xs">
                                {alert.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-white font-medium text-sm">{alert.user_name}</p>
                              <p className="text-amber-300 text-xs">{alert.program_name}</p>
                            </div>
                          </div>
                          <Badge className={`${alert.days_remaining <= 7 ? 'bg-red-600' : alert.days_remaining <= 14 ? 'bg-orange-600' : 'bg-amber-600'} text-white`}>
                            {alert.days_remaining} days left
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>Expires: {new Date(alert.expiry_date).toLocaleDateString()}</span>
                          <Button size="sm" variant="outline" className="h-6 text-xs border-amber-600 text-amber-400 hover:bg-amber-900/50">
                            Schedule Training
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!dashboard?.expiring_alerts || dashboard.expiring_alerts.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <CheckCircle className="w-12 h-12 mb-2 text-emerald-500" />
                        <p>No certifications expiring soon</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reminders Tab */}
        {canSendReminders && (
          <TabsContent value="reminders" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Reminder Schedule Info */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-400" />
                    Reminder Schedule
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Automated notifications are sent at these intervals
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-blue-900/20 rounded border border-blue-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600/30 flex items-center justify-center">
                          <span className="text-blue-400 font-bold">30</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">30 Days Before</p>
                          <p className="text-slate-400 text-xs">Low priority reminder</p>
                        </div>
                      </div>
                      <Badge className="bg-blue-600">Email</Badge>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-amber-900/20 rounded border border-amber-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-600/30 flex items-center justify-center">
                          <span className="text-amber-400 font-bold">14</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">14 Days Before</p>
                          <p className="text-slate-400 text-xs">Medium priority reminder</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-600">Email + Push</Badge>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-orange-900/20 rounded border border-orange-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-600/30 flex items-center justify-center">
                          <span className="text-orange-400 font-bold">7</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">7 Days Before</p>
                          <p className="text-slate-400 text-xs">High priority reminder</p>
                        </div>
                      </div>
                      <Badge className="bg-orange-600">Email + Push</Badge>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-red-900/20 rounded border border-red-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-600/30 flex items-center justify-center">
                          <XCircle className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">On Expiry</p>
                          <p className="text-slate-400 text-xs">Critical alert</p>
                        </div>
                      </div>
                      <Badge className="bg-red-600">All Channels</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Send Bulk Reminders */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-emerald-400" />
                    Send Bulk Reminders
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Send reminders to all users with expiring certifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Button 
                      onClick={() => handleSendBulkReminders(30)}
                      disabled={sendingReminders}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send 30-Day Reminders
                    </Button>
                    
                    <Button 
                      onClick={() => handleSendBulkReminders(14)}
                      disabled={sendingReminders}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send 14-Day Reminders
                    </Button>
                    
                    <Button 
                      onClick={() => handleSendBulkReminders(7)}
                      disabled={sendingReminders}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send 7-Day Reminders
                    </Button>
                    
                    <div className="pt-4 border-t border-slate-700">
                      <Button 
                        onClick={handleRunReminderCheck}
                        disabled={sendingReminders}
                        variant="outline"
                        className="w-full border-emerald-600 text-emerald-400 hover:bg-emerald-900/50"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${sendingReminders ? 'animate-spin' : ''}`} />
                        Run Automated Check
                      </Button>
                      <p className="text-slate-500 text-xs mt-2 text-center">
                        Checks all thresholds and sends appropriate reminders
                      </p>
                    </div>
                    
                    {reminderStatus && (
                      <div className="mt-4 p-3 bg-slate-900 rounded border border-slate-700">
                        <h4 className="text-emerald-400 text-sm font-medium mb-2">Last Reminder Batch</h4>
                        <div className="text-xs text-slate-400 space-y-1">
                          <p>Sent: {reminderStatus.reminders_sent} reminders</p>
                          {reminderStatus.errors?.length > 0 && (
                            <p className="text-red-400">Errors: {reminderStatus.errors.length}</p>
                          )}
                          <p>At: {new Date(reminderStatus.sent_at).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Quick Individual Reminders
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Send reminders to specific users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[350px]">
                    <div className="space-y-2">
                      {/* Show expiring alerts with send button */}
                      {dashboard?.expiring_alerts?.slice(0, 10).map((alert, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/50 rounded border border-slate-700">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{alert.user_name}</p>
                            <p className="text-slate-400 text-xs truncate">{alert.program_name}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Badge className={`text-xs ${alert.days_remaining <= 7 ? 'bg-red-600' : 'bg-amber-600'}`}>
                              {alert.days_remaining}d
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30"
                              onClick={() => handleSendIndividualReminder(alert.user_id, alert.program_id, alert.user_name, alert.program_name)}
                            >
                              <Send className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(!dashboard?.expiring_alerts || dashboard.expiring_alerts.length === 0) && (
                        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                          <CheckCircle className="w-10 h-10 mb-2 text-emerald-500" />
                          <p className="text-sm">No expiring certifications</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Training Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                Training Compliance Matrix
              </CardTitle>
              <CardDescription className="text-slate-400">
                Overview of mandatory training completion across all personnel
              </CardDescription>
            </CardHeader>
            <CardContent>
              {matrix && matrix.programs?.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse min-w-[800px]">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-300 font-medium sticky left-0 bg-slate-800 z-10">Personnel</th>
                          <th className="text-center p-3 text-slate-300 font-medium w-20">Rate</th>
                          {matrix.programs.map(prog => (
                            <th key={prog.id} className="text-center p-2 text-slate-400 text-xs font-medium min-w-[100px]">
                              <div className="truncate max-w-[100px]" title={prog.title}>{prog.title}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {matrix.matrix?.map((row) => (
                          <tr key={row.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                            <td className="p-3 sticky left-0 bg-slate-800/95 z-10">
                              <div className="flex items-center gap-2">
                                <Avatar className="h-7 w-7">
                                  <AvatarFallback className="bg-slate-600 text-white text-xs">
                                    {row.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="text-white text-sm font-medium">{row.user_name}</p>
                                  <p className="text-slate-500 text-xs">{row.role}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <span className={`font-bold ${getComplianceColor(row.compliance_rate)}`}>
                                {row.compliance_rate}%
                              </span>
                            </td>
                            {matrix.programs.map(prog => {
                              const status = row.programs[prog.id];
                              return (
                                <td key={prog.id} className="p-2 text-center">
                                  {status?.applies === false ? (
                                    <span className="text-slate-600">—</span>
                                  ) : status?.status === 'current' ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                                  ) : status?.status === 'expiring_soon' ? (
                                    <Clock className="w-5 h-5 text-amber-500 mx-auto" />
                                  ) : status?.status === 'expired' ? (
                                    <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                                  ) : (
                                    <AlertCircle className="w-5 h-5 text-slate-500 mx-auto" />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Shield className="w-16 h-16 mb-4 opacity-50" />
                  <p>No mandatory training programs configured</p>
                  <p className="text-sm mt-1">Add mandatory training programs to see the compliance matrix</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Matrix Legend */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-slate-300">Current</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="text-slate-300">Expiring Soon</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="text-slate-300">Expired</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-300">Pending Initial</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">—</span>
                  <span className="text-slate-300">Not Required</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
